import { Downloads } from '~api';

/**
 * Pure logic for the admin Files page (`/admin/files`, `add-admin-files-search`): the shareable URL
 * state and its mapping onto `downloads.search`'s input.
 */
export type FilesSearch = {
  q?: string;
  /** @desc Region (market) slug. */
  market?: string;
  group?: string;
  kind?: Downloads.EntryKind;
  /** @desc `YYYY-MM-DD`, inclusive. */
  from?: string;
  to?: string;
  page?: number;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const isDay = (value: unknown): value is string =>
  typeof value === 'string' &&
  DAY.test(value) &&
  new Date(`${value}T00:00:00Z`).toISOString().startsWith(value);

const isSlug = (value: unknown): value is string => typeof value === 'string' && SLUG.test(value);

/**
 * @desc The router's `validateSearch`: keeps what is valid and drops the rest, never throws, so a
 * hand-edited or stale link degrades to the unfiltered view rather than an error page.
 */
export const parseFilesSearch = (raw: Record<string, unknown>): FilesSearch => {
  const search: FilesSearch = {};
  if (typeof raw.q === 'string' && raw.q.trim()) {
    search.q = raw.q.trim().slice(0, Downloads.MAX_SEARCH_QUERY_LENGTH);
  }
  if (isSlug(raw.market)) search.market = raw.market;
  if (isSlug(raw.group)) search.group = raw.group;
  if (Downloads.ALL_ENTRY_KINDS.includes(raw.kind as Downloads.EntryKind)) {
    search.kind = raw.kind as Downloads.EntryKind;
  }
  if (isDay(raw.from)) search.from = raw.from;
  if (isDay(raw.to)) search.to = raw.to;
  const page = Number(raw.page);
  if (Number.isInteger(page) && page > 1) search.page = page;
  return search;
};

export type FilesSearchPatch = { [K in keyof FilesSearch]?: FilesSearch[K] | undefined };

/** @desc Applies a patch; an empty or undefined value removes the key. Any filter change resets the page. */
export const withFilesSearch = (prev: FilesSearch, patch: FilesSearchPatch): FilesSearch => {
  const next: Record<string, unknown> = {
    ...prev,
    ...(patch.page === undefined ? { page: undefined } : {}),
    ...patch,
  };
  for (const key of Object.keys(next)) {
    if (next[key] === undefined || next[key] === '') delete next[key];
  }
  return next as FilesSearch;
};

/** @desc The procedure input for a URL state; the range's end is the end of its day (UTC). */
export const filesSearchInput = (search: FilesSearch): Downloads.SearchInput => ({
  ...(search.q ? { query: search.q } : {}),
  ...(search.market ? { market: search.market } : {}),
  ...(search.group ? { group: search.group } : {}),
  ...(search.kind ? { kind: search.kind } : {}),
  ...(search.from ? { from: new Date(`${search.from}T00:00:00.000Z`) } : {}),
  ...(search.to ? { to: new Date(`${search.to}T23:59:59.999Z`) } : {}),
  page: search.page ?? 1,
});
